import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { AlertService } from '../../../services/alert.service';
import ActionsDropdown from '../../../components/datatable/actions-dropdown.component';
import Header from '../../../components/layout/header.component';
import Asaid from '../../../components/layout/asaid.component';
import Footer from '../../../components/layout/footer.component';
import useBgPages from '../../../hooks/use-bg-pages.hook';
import AddMailingAccount from './modal/add-mailing.modal';
import Datatable from '../../../components/datatable/datatable.component';
import useRequest from '../../../hooks/use-request.hook';
import Icon from '../../../components/forms/icon.component';
import PrincipalCard from '../../../components/cards/principal-card.component';
import { t } from 'i18next';
import DateRegister from '../../../components/datatable/register-data.component';
import { useSession } from '../../../hooks/use-session.hook';
import { PROFILES } from '../../../constants';
import { ChannelsService } from '../../../services/channels.service';
import SendEmail from './modal/send-message.modal';

interface MailingData {
  _id: string;
  created_at: string;
  updated_at?: string;
  status: boolean;
  provider: string;
  name: string;
  settings: {
    host: string;
    port: string;
    name_from: string;
    email_from: string;
    user: string;
    password: string;
  };
}

const StatusBadge = ({ status }: { status: true | false }) => {
  switch (status) {
    case true:
      return (
        <span className="badge text-dark fs-6">
          <Icon icon="ki-check-circle" size="fs-1" type="primary" />
          {t('general.active')}
        </span>
      );
    case false:
      return (
        <span className="badge text-danger fs-6">
          <Icon icon="ki-minus-circle" size="fs-1" type="danger" />
          {t('general.inactive')}
        </span>
      );
    default:
      return <span className="badge badge-secondary">Unknown</span>;
  }
};

function ChannelsMailingPage() {
  useBgPages();
  const { t } = useTranslation();
  const { call, isRequesting } = useRequest();
  const { session } = useSession();

  const [totalAccounts, setTotalAccounts] = useState<number>(0);
  const [totalActives, setTotalActives] = useState<number>(0);
  const [providers, setProviders] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<MailingData[]>([]);

  const [showModal, setShowModal] = useState<{ show: boolean; data: MailingData | null }>({
    show: false,
    data: null
  });
  const [showModalSendMessage, setShowModalSendMessage] = useState<{
    show: boolean;
    data: MailingData | null;
  }>({
    show: false,
    data: null
  });

  const dataStatistics = [
    {
      label: t('channels.mailing.accounts-title'),
      total: totalAccounts,
      type: '',
      icon: 'ki-book-open'
    },
    { label: t('general.active'), total: totalActives, type: 'primary', icon: 'ki-check-circle' }
  ];

  const handleShowModal = async (refresh: boolean) => {
    if (refresh) {
      await getAccounts();
    }
    setShowModal({ show: false, data: null });
  };
  const handleShowModalSendMessage = async (refresh: boolean) => {
    if (refresh) {
      await getAccounts();
    }
    setShowModalSendMessage({ show: false, data: null });
  };

  const renderStatusCell = (row: MailingData) => <StatusBadge status={row.status} />;

  const columns = [
    {
      name: t('channels.mailing.table.account-data'),
      sortable: true,
      selector: (r: any) => `${r.name} ${r.provider}`,
      width: 25,
      cell: (account: MailingData) => {
        const provider = providers.find((p) => p.value === account.provider);
        return (
          <ul className="list-group-item">
            <li>
              <div className="d-flex flex-column">
                <div className="w-100 mb-1">
                  <strong className="fs-6">{t('channels.mailing.table.name')}:</strong>
                  <span className="ms-1">{account.name || t('general.no-info')}</span>
                </div>
                <div className="w-100 mb-1 d-flex align-items-center">
                  <strong className="fs-6">{t('channels.mailing.table.provider')}:</strong>
                  <span className="ms-2 badge badge-light-primary fs-7">
                    {provider?.label || account.provider}
                  </span>
                </div>
              </div>
            </li>
          </ul>
        );
      }
    },
    {
      name: t('channels.mailing.table.status'),
      sortable: true,
      selector: (r: any) => `${r.status}`,
      width: 15,
      cell: renderStatusCell
    },
    {
      name: t('channels.mailing.table.configuration'),
      sortable: true,
      selector: (r: any) =>
        `${r.settings.host} ${r.settings.port} ${r.settings.name_from} ${r.settings.email_from} ${r.settings.user}`,
      width: 35,
      cell: (account: MailingData) => {
        const { host, port, name_from, email_from, user } = account.settings;

        return (
          <ul className="list-group-item">
            <li className="list-group-item">
              <div className="d-flex flex-column">
                <div className="w-100 mb-1 d-flex align-items-center">
                  <Icon icon="ki-cloud" size="fs-3" type="primary" className="me-2" />
                  <strong className="fs-6">{t('channels.mailing.table.host')}:</strong>
                  <span className="ms-1 fs-6">{host || t('general.no-info')}</span>
                </div>
                <div className="w-100 mb-1 d-flex align-items-center">
                  <Icon icon="ki-entrance-right" size="fs-7" type="primary" className="me-2" />
                  <strong className="fs-6">{t('channels.mailing.table.port')}:</strong>
                  <span className="ms-1 fs-6">{port || t('general.no-info')}</span>
                </div>
                <div className="w-100 mb-1 d-flex align-items-center">
                  <Icon icon="ki-profile-user" size="fs-7" type="primary" className="me-2" />
                  <strong className="fs-6">{t('channels.mailing.table.name-from')}:</strong>
                  <span className="ms-1 fs-6">{name_from || t('general.no-info')}</span>
                </div>
                <div className="w-100 mb-1 d-flex align-items-center">
                  <Icon icon="ki-sms" size="fs-7" type="primary" className="me-2" />
                  <strong className="fs-6">{t('channels.mailing.table.email-from')}:</strong>
                  <span className="ms-1 fs-6">{email_from || t('general.no-info')}</span>
                </div>
                <div className="w-100 d-flex align-items-center">
                  <Icon icon="ki-user" size="fs-7" type="primary" className="me-2" />
                  <strong className="fs-6">{t('channels.mailing.table.user')}:</strong>
                  <span className="ms-1 fs-6">{user || t('general.no-info')}</span>
                </div>
              </div>
            </li>
          </ul>
        );
      }
    },
    {
      name: t('channels.mailing.table.register-date'),
      sortable: true,
      selector: (r: any) => `${r.created_at}`,
      width: 15,
      cell: (account: MailingData) => {
        return <DateRegister created_at={account.created_at} />;
      }
    },
    {
      name: t('general.acctions'),
      sortable: true,
      selector: (r: any) => `${r._id}`,
      exportable: false,
      width: 10,
      cell: (account: MailingData) => {
        if (session.profile !== PROFILES.ADMIN) {
          const changeStatus = async (status: boolean) => {
            const confirmed = await AlertService.question(
              t('templates.mailing.confirmations.change-status-title'),
              t('templates.mailing.confirmations.change-status-message', { name: account.name }),
              null as any
            );
            if (!confirmed) {
              return;
            }
            await call(() =>
              ChannelsService.changeStatusMailingAccount({ _id: account._id, status })
            );
            AlertService.toastSuccess(t('templates.mailing.messages.change-status-success'));
            getAccounts();
          };
          return (
            <ActionsDropdown
              edit={() => setShowModal({ show: true, data: account })}
              changeStatus={() => changeStatus(!account.status)}
              rowStatus={account.status}
              send={() => setShowModalSendMessage({ show: true, data: account })}
            />
          );
        } else {
          return <div>-</div>;
        }
      }
    }
  ];

  const getAccounts = async () => {
    try {
      const accountsData = await call(() => ChannelsService.getMailingAccounts());

      setTotalActives(accountsData.filter((account: any) => account.status === true).length);
      setTotalAccounts(accountsData.length);
      setAccounts(accountsData);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };
  const getProviders = async () => {
    try {
      const data = await ChannelsService.getMailingProviders();
      setProviders(
        data.map((item: any) => ({
          value: item._id,
          label: item.name
        }))
      );
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getProviders();
        await getAccounts();
      } catch (errorCode: any) {
        console.log('Error:', errorCode);
        AlertService.toastError(errorCode.error || errorCode);
      }
    };
    fetchData();
  }, []);

  return (
    <>
      <div className="page d-flex flex-row flex-column-fluid">
        <Asaid />
        <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
          <Header />
          <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
            <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
              <div className="container-xxl">
                <PrincipalCard
                  dataStatistics={dataStatistics}
                  btnCreate={session.profile !== PROFILES.ADMIN}
                  onClick={() => setShowModal({ show: true, data: null })}
                  btnRefresh
                  onRefresh={async () => {
                    await getAccounts();
                    AlertService.toastSuccess(t('general.refresh-success'));
                  }}
                  btnBack
                  title={t('channels.mailing.title')}
                  titleBolder={t('channels.mailing.title-bold')}
                  subtitle={t('channels.mailing.subtitle')}
                  breadcrumbItems={t('channels.mailing.breadcrumb', { returnObjects: true })}
                  textButton={t('channels.mailing.register-button')}
                  iconButton="ki-soft-2"
                />
                <div className="card mb-5 mb-xl-10">
                  <div className="card-header align-items-center border-0 mt-5">
                    <div className="card-title align-items-start flex-column animate-fadeInUp animate-delay-1 h3">
                      <span className="fw-bolder fw-bold fs-2">
                        {t('channels.mailing.accounts-title')}
                      </span>
                      <span className="text-gray-700 mt-2 fw-semibold fs-6">
                        {t('channels.mailing.accounts-registered')}
                      </span>
                    </div>
                    <div className="card-toolbar"></div>
                  </div>
                  <div className="card-body pt-1">
                    <Datatable columns={columns} data={accounts} isLoading={isRequesting} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
          <AddMailingAccount
            show={showModal.show}
            onRequestClose={(refresh: any) => handleShowModal(refresh)}
            payload={showModal.data}
          />
          <SendEmail
            show={showModalSendMessage.show}
            payload={showModalSendMessage.data}
            onRequestClose={(refresh: any) => handleShowModalSendMessage(refresh)}
          />
        </div>
      </div>
    </>
  );
}

export default ChannelsMailingPage;
